import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  computed,
  effect,
  inject,
  input,
  Output,
  untracked,
} from '@angular/core';

import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ButtonTheme, TagTheme, IconPosition, LinkSize } from '@fcom/ui-components';
import { Category } from '@fcom/dapi/api/models/category';
import { FinnairServiceVariantItemV3 } from '@fcom/dapi/api/models/finnair-service-variant-item-v-3';
import { getIconForServiceCategory } from '@fcom/dapi/utils/service-category-utils';
import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { GA4Category, GtmEvent, GtmPurchaseFlow } from '@fcom/common/interfaces/gtm.interface';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { getAffiliation, getGA4Id, getHaulTypeFromBounds } from '@fcom/common/utils/gtm.utils';
import { cartDataSelector } from '@fcom/booking/store/selectors/cart.selector';

import {
  bundleProductInfoSelector,
  passengerCanAddBundleSelector,
  passengerHasSpecificBundleSelector,
  seatBundleLowestPriceForFlightAndCategorySelector,
  bundleServiceMatchesForSeatSelector,
} from '../../../../../store/selectors/bundle.selector';
import { matchingSeatBundleOfferServicesSelector } from '../../../../../store/selectors/ui-action.selector';
import { orderDataSelector } from '../../../../../store/selectors/order.selector';
import {
  originalRegularSeatByFlightSelector,
  seatMapForFlight,
} from '../../../../../store/selectors/seat-map.selector';
import { toAmount } from '../../../../../utils/common-booking.utils';
import { selectedSeatNumberByFlightAndPassengerSelector } from '../seat-map-components.selector';

export enum BundlePromotionCardVariant {
  /** Generic card when pax doesn't have a seat selected */
  NOT_INTERACTIVE = 'not-interactive',
  /** When pax has regular seat and can upgrade it to a seat bundle for the seat. */
  UPGRADE_SEAT_TO_BUNDLE = 'upgrade-seat-to-bundle',
  /** When pax has upgraded seat to seat bundle, they should see an option to downgrade back to regular seat. */
  DOWNGRADE_TO_SEAT = 'downgrade-to-seat',
  /** When pax has added a seat bundle as a whole, they should see an option to remove it as a whole. */
  REMOVE_BUNDLE = 'remove-bundle',
}

@Component({
  selector: 'fin-bundle-promotion-card',
  templateUrl: './bundle-promotion-card.component.html',
  styleUrls: ['./bundle-promotion-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BundlePromotionCardComponent {
  private store$ = inject(Store);
  private gtmService = inject(GtmService);

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconPosition = IconPosition;
  readonly LinkSize = LinkSize;
  readonly TagTheme = TagTheme;
  readonly ButtonTheme = ButtonTheme;
  readonly BundlePromotionCardVariant = BundlePromotionCardVariant;

  readonly forceNotInteractive = input(false);
  readonly category = input.required<Category>();
  readonly categoryIcon = computed(() => getIconForServiceCategory(this.category()));
  private readonly serviceInfoSelectorForCurrentCategory = computed(() => bundleProductInfoSelector(this.category()));
  readonly serviceInfo = computed(() => this.store$.selectSignal(this.serviceInfoSelectorForCurrentCategory())());

  readonly flightId = input.required<string>();
  readonly passengerId = input.required<string>();
  readonly purchaseFlow = input.required<GtmPurchaseFlow>();

  private readonly offeredBundleServicesSelector = computed(() =>
    matchingSeatBundleOfferServicesSelector(this.category(), this.flightId(), this.passengerId())
  );

  readonly bundleTranslationKey = computed(() => {
    const [offeredService] = this.store$.selectSignal(this.offeredBundleServicesSelector())();
    return this.category() === Category.SEAT_WIFI
      ? offeredService?.parameters?.variantTag
      : offeredService?.subCategory;
  });

  private readonly lowestPriceSelectorForFlightAndCategory = computed(() =>
    seatBundleLowestPriceForFlightAndCategorySelector(this.flightId(), this.category())
  );

  readonly bundleOffersLowestPrice = computed(() =>
    this.store$.selectSignal(this.lowestPriceSelectorForFlightAndCategory())()
  );

  private readonly bundleForPassengerProps = computed(() => ({
    flightId: this.flightId(),
    travelerId: this.passengerId(),
    category: this.category(),
  }));
  private readonly canAddBundleSelector = computed(() => passengerCanAddBundleSelector(this.bundleForPassengerProps()));
  private readonly canAddBundle = computed(() => this.store$.selectSignal(this.canAddBundleSelector())());
  private readonly holdsBundleSelector = computed(() =>
    passengerHasSpecificBundleSelector(this.bundleForPassengerProps())
  );
  private readonly holdsBundle = computed(() => this.store$.selectSignal(this.holdsBundleSelector())());

  private readonly seatMapSelectorForFlight = computed(() => seatMapForFlight(this.flightId()));
  private readonly seatMap = computed(() => this.store$.selectSignal(this.seatMapSelectorForFlight())());
  private readonly selectedSeatNumbers = this.store$.selectSignal(selectedSeatNumberByFlightAndPassengerSelector);
  private readonly selectedSeatNumber = computed(
    () => this.selectedSeatNumbers()[this.flightId()]?.[this.passengerId()]
  );
  private readonly bundleSeatMatches = this.store$.selectSignal(bundleServiceMatchesForSeatSelector);
  private readonly bundleServiceForSelectedSeat = computed(
    () =>
      (
        this.bundleSeatMatches()[this.flightId()] as Record<
          string,
          Record<string, Record<string, FinnairServiceVariantItemV3>>
        >
      )?.[this.passengerId()]?.[this.category()]?.[this.selectedSeatNumber() ?? '']
  );
  private readonly originalRegularSeats = this.store$.selectSignal(originalRegularSeatByFlightSelector);
  private readonly hadRegularSeat = computed(
    () => !!this.originalRegularSeats()[this.flightId()]?.[this.passengerId()]
  );

  private readonly cartData = this.store$.selectSignal(cartDataSelector);
  private readonly orderData = this.store$.selectSignal(orderDataSelector);

  /**
   * GTM tracking for this use of view_item_list requires a one-based index of the passenger being viewed.
   */
  private readonly passengerIndex = computed(() => {
    const passengers = this.cartData()?.passengers ?? this.orderData()?.passengers ?? [];
    return passengers.findIndex((passenger) => passenger.id === this.passengerId()) + 1;
  });

  readonly bundleUpsellPrice = computed<Amount | undefined>(() => {
    const seatNumber = this.selectedSeatNumber();
    if (!seatNumber || !this.canAddBundle()) {
      return undefined;
    }
    const regularSeatPrice = this.seatMap()
      ?.seats?.find((seat) => seat.seatNumber === seatNumber)
      ?.prices?.find((price) => price.travelerIds?.includes(this.passengerId()))?.price;
    const bundleForSeat = this.bundleServiceForSelectedSeat();
    if (regularSeatPrice?.amount === undefined || bundleForSeat?.totalPrice?.amount === undefined) {
      return undefined;
    }
    return toAmount({
      amount: (Number(bundleForSeat.totalPrice.amount) - Number(regularSeatPrice.amount)).toString(),
      currencyCode: regularSeatPrice.currencyCode,
    });
  });

  readonly variant = computed<BundlePromotionCardVariant>(() => {
    if (this.forceNotInteractive() || !this.selectedSeatNumber() || (!this.canAddBundle() && !this.holdsBundle())) {
      return BundlePromotionCardVariant.NOT_INTERACTIVE;
    }
    if (this.bundleUpsellPrice()) {
      return BundlePromotionCardVariant.UPGRADE_SEAT_TO_BUNDLE;
    }
    if (this.holdsBundle()) {
      return this.hadRegularSeat()
        ? BundlePromotionCardVariant.DOWNGRADE_TO_SEAT
        : BundlePromotionCardVariant.REMOVE_BUNDLE;
    }
    // Show generic version when user has regular seat but we can't calculate upsell price.
    // Or for any other unusual scenario fallback.
    return BundlePromotionCardVariant.NOT_INTERACTIVE;
  });

  readonly bundleHeld = computed(
    () =>
      this.variant() === BundlePromotionCardVariant.DOWNGRADE_TO_SEAT ||
      this.variant() === BundlePromotionCardVariant.REMOVE_BUNDLE
  );

  readonly accented = computed(() => this.variant() !== BundlePromotionCardVariant.UPGRADE_SEAT_TO_BUNDLE);

  @Output()
  toggled: EventEmitter<void> = new EventEmitter();

  openModal = false;

  constructor() {
    effect(() => {
      // Tracked reads: pointing the card at another passenger/flight is a new promotion impression.
      this.flightId();
      this.passengerId();
      // The rest of the payload resolves from the same store snapshot as the card's own content, so it is
      // read untracked: there is nothing to wait for, and a later price or cart update must not report the
      // impression again.
      untracked(() => this.reportPromotionImpression());
    });
  }

  emitToggle(): void {
    this.toggled.emit();
  }

  private reportPromotionImpression(): void {
    if (!this.canAddBundle()) {
      return;
    }
    const listId = `booking_seatmap_promotion-pax${this.passengerIndex()}`;
    this.gtmService.ecommerceEventGA4(GtmEvent.VIEW_ITEM_LIST, [
      {
        item_id: getGA4Id(this.category()),
        item_name: getGA4Id(this.category()),
        price: 0,
        quantity: 1,
        currency: this.bundleUpsellPrice()?.currencyCode,
        affiliation: getAffiliation(this.purchaseFlow()),
        item_bundle: true,
        item_category: GA4Category.ANCILLARIES,
        item_category2: 'Bundle',
        item_category3: '',
        item_variant: undefined,
        item_list_id: listId,
        item_list_name: listId,
        item_haul_type: getHaulTypeFromBounds(this.cartData()?.bounds || this.orderData()?.bounds || []),
        item_complementary: false,
        creative_slot: this.hadRegularSeat() ? 'bundle-available' : 'seat-selection-missing',
      },
    ]);
  }

  protected readonly getIconForServiceCategory = getIconForServiceCategory;
  protected readonly Category = Category;
}
